import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import Icon from './AppIcon';
import {
  useUserProfiles,
  ROLE_OPTIONS,
  DISCIPLINE_OPTIONS,
} from '../context/UserProfilesContext';
import { deriveNameFromEmail } from '../lib/currentUser';

// ---------------------------------------------------------------------------
// ProfileSetupPrompt
//
// A one-time modal that appears after a real signed-in user's first
// login, asking them to fill in the two fields the Collaboration Hub
// needs to make attribution meaningful: display_name and role
// (+ discipline for related-service providers).
//
// It renders whenever `needsProfileSetup` is true on the context — the
// hub itself has a "Complete profile" link that also flips the same
// flag on demand, so this component can be reused as a "finish setting
// up your profile" nudge later.
//
// It's intentionally skipped on the /login and /forgot-password /
// /reset-password routes so the modal doesn't cover the sign-in form.
// ---------------------------------------------------------------------------

const HIDDEN_ROUTES = new Set([
  '/login',
  '/forgot-password',
  '/reset-password',
]);

// Routes that render their own bespoke shell (print sheet, etc.) and
// really shouldn't get a modal slapped on top. Anything under /print/*.
function shouldHideOnRoute(pathname) {
  if (!pathname) return false;
  if (HIDDEN_ROUTES.has(pathname)) return true;
  if (pathname.startsWith('/print/')) return true;
  return false;
}

const ProfileSetupPrompt = () => {
  const location = useLocation();
  const {
    isLive,
    me,
    needsProfileSetup,
    updateMyProfile,
    loading,
  } = useUserProfiles();

  const [dismissed, setDismissed] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [customDiscipline, setCustomDiscipline] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Pre-fill from whatever we know: the auth session email → derived name,
  // and the role the user picked on the login screen (in demo mode the
  // context stub carries this through).
  useEffect(() => {
    if (!me) return;
    setDisplayName((prev) => prev || me.displayName || deriveNameFromEmail(me.email));
    setRole((prev) => prev || me.role || '');
    setDiscipline((prev) => prev || me.discipline || '');
  }, [me]);

  const open =
    !dismissed &&
    !loading &&
    needsProfileSetup &&
    !shouldHideOnRoute(location.pathname);

  if (!open) return null;

  const chosenDiscipline =
    role === 'related_service'
      ? (discipline === '__other__' ? customDiscipline.trim() : discipline)
      : '';

  const canSave = Boolean(displayName.trim() && role);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!canSave || saving) return;
    setSaving(true);
    setErr('');
    try {
      await updateMyProfile({
        displayName: displayName.trim(),
        role,
        discipline: chosenDiscipline || null,
      });
      setDismissed(true);
    } catch (error) {
      setErr(error?.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => setDismissed(true);

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-setup-title"
    >
      <form
        onSubmit={handleSave}
        className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl bg-card shadow-elevated border border-border"
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
              <Icon name="Users" size={22} />
            </div>
            <div className="flex-1">
              <h2
                id="profile-setup-title"
                className="text-xl font-semibold text-card-foreground"
              >
                Welcome to your team's data hub
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {isLive
                  ? 'Tell your teammates who you are. Your name and role show up on every data point you log so the IEP team knows who to ask questions.'
                  : "You're in demo mode. This is what your teammates would see the first time they sign in."}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <Input
            label="Your name"
            placeholder="e.g. Jane Rivera"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            description="Shows on your data points and in the team roster."
            required
          />

          <Select
            label="Your role"
            placeholder="Choose your role"
            options={ROLE_OPTIONS}
            value={role}
            onChange={(next) => {
              setRole(next);
              if (next !== 'related_service') {
                setDiscipline('');
                setCustomDiscipline('');
              }
            }}
            required
          />

          {role === 'related_service' && (
            <div className="space-y-2">
              <Select
                label="Specialty"
                placeholder="Choose your specialty"
                options={[
                  ...DISCIPLINE_OPTIONS,
                  { value: '__other__', label: 'Other (type it in)' },
                ]}
                value={discipline}
                onChange={setDiscipline}
                description="Speech, OT, PT, psych, etc. Shows next to your name on shared reports."
              />
              {discipline === '__other__' && (
                <Input
                  placeholder="e.g. Vision specialist"
                  value={customDiscipline}
                  onChange={(e) => setCustomDiscipline(e.target.value)}
                />
              )}
            </div>
          )}

          {err && (
            <p className="text-sm text-destructive" role="alert">
              {err}
            </p>
          )}
        </div>

        <div className="p-6 pt-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={handleSkip}
            disabled={saving}
          >
            Not now
          </Button>
          <Button
            type="submit"
            variant="default"
            loading={saving}
            disabled={!canSave || saving}
            iconName="Check"
            iconPosition="left"
            iconSize={16}
          >
            Save and continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileSetupPrompt;
